import React, { FC } from 'react';
import { getLayouts } from './__queries__';
import SelectList from '~ComponentsImproved/SelectList';
import { addToast } from '~actions';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import useI18n from '~Translations';
import { useDispatch, useLogger, useSelector } from '~Hooks';
import Loader from '~ComponentsImproved/Loader';
import { SettingsBlock, SettingsLoaderWrapper } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import Toggle from '~ComponentsImproved/Toggle';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import { StorageData, useStorage } from '~Components/Storage';
import { TogglesConfig, isHiddenOnDevice } from '~Scenes/Settings-v2/types';

const GET_LAYOUTS = gql`
  query getLayouts($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        layouts {
          id
          name {
            id
            se
            en
            es
            no
          }
        }
      }
    }
  }
`;

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

const PosLayoutSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const logger = useLogger('settings-v2');
  const [storage, updateStorage, storageLoading] = useStorage();

  const venueId = useSelector((state) => state.venueId);
  const POSLayout = useSelector((state) => state.POSLayout);

  const layoutsQuery = useQuery<getLayouts>(GET_LAYOUTS, {
    variables: { venueId },
    skip: !venueId,
  });

  const updatePOSLayout = (layout: string) => {
    dispatch({
      type: 'SET_POS_LAYOUT',
      POSLayout: layout,
    });
    window.postMessageBridge({
      devicePOSLayout: layout,
    });

    logger.info('Updating POS Layout', { fields: { layout } });
    dispatch(addToast(i18n('TOAST.Layout.Updated'), true));
  };

  if (layoutsQuery.loading || storageLoading) {
    return (
      <SettingsLoaderWrapper>
        <Loader />
      </SettingsLoaderWrapper>
    );
  }

  const layouts = layoutsQuery.data?.venue?.company?.layouts ?? [];
  const layoutList = layouts.map((layout) => ({
    id: layout.id,
    name: i18n(layout.name),
  }));

  const togglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.PosLayout.ShowPrices'),
      key: 'showPricesOnButtons',
      storage: storage.showPricesOnButtons,
    },
    {
      label: i18n('Settings.PosLayout.ShowImages'),
      key: 'showImagesOnButtons',
      storage: storage.showImagesOnButtons,
    },
    {
      label: i18n('Settings.PosLayout.RequireBeeper'),
      subLabel: i18n('Settings.PosLayout.RequireBeeper.Desc'),
      key: 'requireBeeperNumber',
      storage: storage.requireBeeperNumber,
    },
    {
      label: i18n('Settings.StayOnCategoryAfterPayment'),
      subLabel: i18n('Settings.StayOnCategoryAfterPayment.Text'),
      key: 'stayOnCategoryAfterPayment',
      storage: storage.stayOnCategoryAfterPayment,
    },
  ];

  return (
    <>
      <Box padding={isHandy ? 'none' : 'large'} marginTop={isHandy ? 'small' : 'none'}>
        <SettingsBlock compact={isHandy}>
          <SelectList
            compact={isHandy}
            label={i18n('Settings.PosLayout')}
            list={[
              {
                id: 'NONE',
                name: i18n('Settings.LayoutNone') + ' ' + i18n('Settings.LayoutUseCategories'),
              },
              ...layoutList,
            ]}
            selectedId={POSLayout ?? 'NONE'}
            onSelect={updatePOSLayout}
          />
          {togglesConfig.map(
            (toggle) =>
              !isHiddenOnDevice(toggle.hideIn, isHandy) && (
                <Toggle
                  key={toggle.key}
                  settingKey={toggle.key}
                  label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
                  description={toggle.subLabel}
                  initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
                  onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
                  fullWidth
                  darkBackground
                  lightText
                  compact={isHandy}
                />
              ),
          )}
          <RadioButtonSelect
            settingKey="articleTextSize"
            disabled={storage.showImagesOnButtons}
            data={{
              containerLabel: i18n('Settings.ArticleTextSize'),
              containerDesc: storage.showImagesOnButtons
                ? i18n('Settings.ArticleTextSize.DisabledByImages')
                : undefined,
              name: 'articleTextSize',
              buttons: [
                {
                  id: 'standard',
                  label: i18n('Settings.ArticleTextSize.Medium'),
                  value: 'standard',
                },
                { id: 'big', label: i18n('Settings.ArticleTextSize.Large'), value: 'big' },
                {
                  id: 'bigger',
                  label: i18n('Settings.ArticleTextSize.XL'),
                  value: 'bigger',
                },
              ],
            }}
            selected={storage.articleTextSize}
            compact={isHandy}
            handleChange={(value) => {
              logger.info('Updating articleTextSize', {
                from: storage.articleTextSize,
                to: value,
              });
              updateStorage({ articleTextSize: value as StorageData['articleTextSize'] });
            }}
          />
        </SettingsBlock>
      </Box>
    </>
  );
};

export default PosLayoutSettings;
